/**
 * Google Tag Manager Integration
 * Handles GTM initialization and data layer management
 */

export interface GTMConfig {
  gtmId: string;
  dataLayer?: string;
  dataLayerName?: string;
}

export interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    dataLayer: DataLayerEvent[];
  }
}

class GTMManager {
  private gtmId: string;
  private dataLayerName: string;
  private initialized = false;

  constructor() {
    this.gtmId = '';
    this.dataLayerName = 'dataLayer';
  }

  /**
   * Initialize Google Tag Manager
   */
  initialize(config: GTMConfig): void {
    if (this.initialized) {
      console.warn('GTM already initialized');
      return;
    }

    if (!config.gtmId) {
      console.error('GTM ID is required');
      return;
    }

    this.gtmId = config.gtmId;
    this.dataLayerName = config.dataLayerName || 'dataLayer';

    // Initialize data layer
    window.dataLayer = window.dataLayer || [];

    // Add GTM script
    this.injectGTMScript();
    this.injectGTMNoScript();

    this.initialized = true;
  }

  /**
   * Inject GTM script into document head
   */
  private injectGTMScript(): void {
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','${this.dataLayerName}','${this.gtmId}');
    `;
    document.head.insertBefore(script, document.head.childNodes[0]);
  }

  /**
   * Inject GTM noscript fallback into document body
   */
  private injectGTMNoScript(): void {
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.childNodes[0]);
  }

  /**
   * Push event to data layer
   */
  pushEvent(event: DataLayerEvent): void {
    if (!this.initialized) {
      console.warn('GTM not initialized. Call initialize() first.');
      return;
    }

    if (!window.dataLayer) {
      console.error('Data layer not available');
      return;
    }

    window.dataLayer.push(event);
  }

  /**
   * Push multiple events to data layer
   */
  pushEvents(events: DataLayerEvent[]): void {
    events.forEach(event => this.pushEvent(event));
  }

  /**
   * Check if GTM is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

export const gtmManager = new GTMManager();
